import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  CreditCard,
  Filter,
  RefreshCw,
  Users,
  Zap,
  Package,
  FileText,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import api from "@/services/api";
import { generateUUID } from "@/utils/transform";

// Event category configuration
const EVENT_CATEGORIES = {
  credits: {
    label: "Credits",
    icon: CreditCard,
    color: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  },
  team: {
    label: "Team",
    icon: Users,
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  },
  plan: {
    label: "Plan",
    icon: Package,
    color:
      "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  },
  addon: {
    label: "Add-on",
    icon: Zap,
    color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  },
  usage: {
    label: "Usage",
    icon: Activity,
    color: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200",
  },
  api: {
    label: "API",
    icon: FileText,
    color: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  },
};

// Event type labels
const EVENT_TYPE_LABELS = {
  credit_used: "Credit Used",
  credit_purchased: "Credit Purchased",
  credit_added: "Credit Added",
  credit_deducted: "Credit Deducted",
  team_member_added: "Member Added",
  team_member_removed: "Member Removed",
  team_member_role_changed: "Role Changed",
  plan_changed: "Plan Changed",
  plan_upgraded: "Plan Upgraded",
  plan_downgraded: "Plan Downgraded",
  plan_cancelled: "Plan Cancelled",
  addon_activated: "Add-on Activated",
  addon_upgraded: "Add-on Upgraded",
  addon_downgraded: "Add-on Downgraded",
  addon_cancelled: "Add-on Cancelled",
  addon_reactivated: "Add-on Reactivated",
  envelope_sent: "Envelope Sent",
  sms_sent: "SMS Sent",
  api_call: "API Call",
  bulk_send: "Bulk Send",
};

// Category badge component
const CategoryBadge = ({ category }) => {
  const config = EVENT_CATEGORIES[category] || {
    label: category,
    icon: Activity,
    color: "bg-gray-100 text-gray-800",
  };
  const Icon = config.icon;

  return (
    <Badge variant="secondary" className={`gap-1 ${config.color}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

// Billed status badge
const BilledBadge = ({ billed }) => {
  if (billed) {
    return (
      <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
        Billed
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      Unbilled
    </Badge>
  );
};

export const UsageLogViewer = ({ organisationId, organisationName }) => {
  // Filter state
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    eventCategory: "",
    eventType: "",
    billed: "",
  });
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // Build query params
  const queryParams = useMemo(() => {
    const params = { page, limit: 25 };
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.eventCategory) params.eventCategory = filters.eventCategory;
    if (filters.eventType) params.eventType = filters.eventType;
    if (filters.billed) params.billed = filters.billed;
    return params;
  }, [page, filters]);

  // Fetch usage log
  const {
    data: logData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["admin", "usage-log", organisationId, queryParams],
    queryFn: async () => {
      const params = new URLSearchParams(queryParams);
      const response = await api.get(
        `/admin/organisations/${organisationId}/usage-log?${params}`,
      );
      return response.data?.data;
    },
    enabled: !!organisationId,
    placeholderData: (prev) => prev,
  });

  const logs = logData?.items || [];
  const pagination = logData?.pagination || {
    page: 1,
    totalPages: 1,
    total: 0,
  };

  // Get unique event types for current category
  const eventTypesForCategory = useMemo(() => {
    if (!filters.eventCategory) return Object.keys(EVENT_TYPE_LABELS);

    const categoryPrefixes = {
      credits: ["credit_"],
      team: ["team_member_"],
      plan: ["plan_"],
      addon: ["addon_"],
      usage: ["envelope_", "sms_", "bulk_"],
      api: ["api_"],
    };

    const prefixes = categoryPrefixes[filters.eventCategory] || [];
    return Object.keys(EVENT_TYPE_LABELS).filter((type) =>
      prefixes.some((prefix) => type.startsWith(prefix)),
    );
  }, [filters.eventCategory]);

  // Clear filters
  const clearFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      eventCategory: "",
      eventType: "",
      billed: "",
    });
    setPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some((v) => v !== "");

  // Format currency
  const formatAmount = (amount) => {
    if (!amount || amount === 0) return "-";
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  // Human-readable bytes
  const formatBytes = (bytes) => {
    const n = Number(bytes);
    if (!n || n <= 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
    return `${(n / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  };

  // Quantity column: storage events show human-readable bytes, others a count
  const formatQuantity = (log) => {
    if (log.event_type === "storage_used") return formatBytes(log.quantity);
    return log.quantity || "-";
  };

  // Format date
  const formatDate = (dateStr) => new Date(dateStr).toLocaleString();

  return (
    <Card className="border-border">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-foreground">
              Usage Audit Log
            </CardTitle>
            {organisationName && (
              <p className="text-sm text-muted-foreground mt-1">
                {organisationName}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={hasActiveFilters ? "border-primary" : ""}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                  {Object.values(filters).filter((v) => v !== "").length}
                </Badge>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw
                className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 p-4 bg-muted/50 rounded-lg space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Start Date</Label>
                <DatePicker
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, startDate: e.target.value }))
                  }
                  className="h-9"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">End Date</Label>
                <DatePicker
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, endDate: e.target.value }))
                  }
                  className="h-9"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Category</Label>
                <Select
                  value={filters.eventCategory}
                  onValueChange={(v) =>
                    setFilters((f) => ({
                      ...f,
                      eventCategory: v === "all" ? "" : v,
                      eventType: "",
                    }))
                  }
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {Object.entries(EVENT_CATEGORIES).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Event Type</Label>
                <Select
                  value={filters.eventType}
                  onValueChange={(v) =>
                    setFilters((f) => ({
                      ...f,
                      eventType: v === "all" ? "" : v,
                    }))
                  }
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {eventTypesForCategory.map((type) => (
                      <SelectItem key={type} value={type}>
                        {EVENT_TYPE_LABELS[type] || type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Billing Status</Label>
                <Select
                  value={filters.billed}
                  onValueChange={(v) =>
                    setFilters((f) => ({
                      ...f,
                      billed: v === "all" ? "" : v,
                    }))
                  }
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="false">Unbilled</SelectItem>
                    <SelectItem value="true">Billed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {hasActiveFilters && (
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear filters
                </Button>
              </div>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent>
        {isLoading && (
          <div className="space-y-3">
            {[...Array(5)].map(() => (
              <Skeleton key={generateUUID()} className="h-12 w-full" />
            ))}
          </div>
        )}

        {!isLoading && logs.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No usage events found</p>
            {hasActiveFilters && (
              <Button
                variant="link"
                size="sm"
                onClick={clearFilters}
                className="mt-2"
              >
                Clear filters
              </Button>
            )}
          </div>
        )}

        {!isLoading && logs.length > 0 && (
          <>
            <div className="border border-border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-45">Date</TableHead>
                    <TableHead className="w-25">Category</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead className="w-20 text-right">Qty</TableHead>
                    <TableHead className="w-25 text-right">Amount</TableHead>
                    <TableHead className="w-20 text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} className="group">
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <CategoryBadge category={log.event_category} />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-foreground text-sm">
                            {EVENT_TYPE_LABELS[log.event_type] ||
                              log.event_type}
                          </p>
                          <p className="text-xs text-muted-foreground truncate max-w-75">
                            {log.description}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatQuantity(log)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatAmount(log.total_amount)}
                      </TableCell>
                      <TableCell className="text-center">
                        <BilledBadge billed={log.billed} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Showing {logs.length} of {pagination.total} events
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  Page {page} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= pagination.totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default UsageLogViewer;
